import React, { useState, useEffect, useRef } from "react";
import { Zap, Check, Calendar, Clock } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import api from "../../lib/api";

const DEFAULT_QUICK_TIMES = [
  { id: "today-14", label: "Hoje 14:00", hour: 14, minute: 0, day_offset: 0 },
  { id: "tomorrow-10", label: "Amanha 10:00", hour: 10, minute: 0, day_offset: 1 },
  { id: "two-days-15", label: "Em 2 dias 15:00", hour: 15, minute: 0, day_offset: 2 },
  { id: "next-week", label: "Proxima semana", hour: 10, minute: 0, day_offset: 7 },
];

function buildScheduleMessage(leadName, slot) {
  const date = new Date();
  date.setDate(date.getDate() + (slot.day_offset || 0));
  date.setHours(slot.hour || 0, slot.minute || 0, 0, 0);
  const day = date.toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long" });
  const time = date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  return `Ola ${leadName}! Podemos marcar uma reuniao rapida em *${day}* as *${time}*? Se preferir outro horario, e so me avisar.`;
}

export default function QuickActions({ lead, onPickMessage, googleConnected }) {
  const [open, setOpen] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [scheduleShortcuts, setScheduleShortcuts] = useState(DEFAULT_QUICK_TIMES);
  const ref = useRef(null);

  const applyProductivityData = (productivity, fallbackTemplates = []) => {
    const configuredTemplates = Array.isArray(productivity?.custom_templates) ? productivity.custom_templates : [];
    const approvedOfficialTemplates = Array.isArray(productivity?.official_whatsapp_templates)
      ? productivity.official_whatsapp_templates
          .filter((template) => template?.status === "APPROVED")
          .map((template) => ({
            id: `waba-${template.id}`,
            title: template.name,
            content: template.body,
          }))
      : [];
    const configuredShortcuts = Array.isArray(productivity?.schedule_shortcuts) ? productivity.schedule_shortcuts : [];
    const nextTemplates = configuredTemplates.length
      ? [
          ...configuredTemplates,
          ...approvedOfficialTemplates.filter(
            (official) => !configuredTemplates.some((template) => template.id === official.id)
          ),
        ]
      : [...approvedOfficialTemplates, ...(fallbackTemplates || [])];
    setTemplates(nextTemplates);
    setScheduleShortcuts(configuredShortcuts.length ? configuredShortcuts : DEFAULT_QUICK_TIMES);
  };

  useEffect(() => {
    const loadProductivity = () => Promise.all([api.get("/settings/productivity"), api.get("/templates")])
      .then(([productivityRes, templatesRes]) => {
        applyProductivityData(productivityRes?.data || {}, templatesRes?.data || []);
      })
      .catch(() => {});
    loadProductivity();
    const handleProductivityUpdate = (event) => {
      applyProductivityData(event?.detail || {});
    };
    window.addEventListener("productivity-settings-updated", handleProductivityUpdate);
    return () => window.removeEventListener("productivity-settings-updated", handleProductivityUpdate);
  }, []);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(null); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const applyTemplate = (tmpl) => {
    // Placeholders fixos + qualquer campo personalizado do tenant presente
    // no lead (lead.custom_fields) — mesma ideia do _render_message do
    // backend (automations.py), só que client-side pra este envio de 1 clique.
    const values = {
      nome: lead?.name || "",
      empresa: lead?.company || "",
      telefone: lead?.phone || (lead?.remote_jid || "").split("@")[0] || "",
      ...(lead?.custom_fields && typeof lead.custom_fields === "object" ? lead.custom_fields : {}),
    };
    let text = tmpl?.content || "";
    Object.entries(values).forEach(([key, value]) => {
      if (!key) return;
      text = text.replace(new RegExp(`\\{${key}\\}`, "gi"), value != null ? String(value) : "");
    });
    onPickMessage?.(text);
    setOpen(null);
  };

  const applySchedule = (slot) => {
    onPickMessage?.(buildScheduleMessage(lead?.name || "", slot));
    setOpen(null);
  };

  return (
    <div className="relative flex items-center gap-1" ref={ref}>
      <button
        data-testid="templates-btn"
        onClick={() => setOpen(open === "templates" ? null : "templates")}
        className={`text-[11px] font-semibold flex items-center gap-1 px-2 py-1 rounded-md transition ${
          open === "templates"
            ? "bg-[#25D366]/15 text-[#25D366] border border-[#25D366]/40"
            : "text-slate-400 hover:text-slate-100 hover:bg-slate-800 border border-transparent"
        }`}
        title="Templates de mensagem"
      >
        <Zap className="w-3.5 h-3.5" /> Templates
      </button>

      <button
        data-testid="schedule-quick-btn"
        onClick={() => setOpen(open === "schedule" ? null : "schedule")}
        className={`text-[11px] font-semibold flex items-center gap-1 px-2 py-1 rounded-md transition ${
          open === "schedule"
            ? "bg-[#25D366]/15 text-[#25D366] border border-[#25D366]/40"
            : "text-slate-400 hover:text-slate-100 hover:bg-slate-800 border border-transparent"
        }`}
        title="Agendar reuniao"
      >
        <Calendar className="w-3.5 h-3.5" /> Agendar
      </button>

      <AnimatePresence>
        {open === "templates" && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.15 }}
            className="absolute left-0 top-full mt-2 w-80 bg-slate-900 border border-slate-700 rounded-xl shadow-2xl z-30 p-2 max-h-80 overflow-y-auto"
            data-testid="templates-panel"
          >
            <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              Templates de 1 clique
            </div>
            {templates.length === 0 && (
              <div className="py-4 text-center text-xs text-slate-500">Nenhum template ainda</div>
            )}
            {templates.map((t) => (
              <button
                key={t.id}
                data-testid={`template-${t.id}`}
                onClick={() => applyTemplate(t)}
                className="w-full text-left px-2.5 py-2 rounded-md hover:bg-slate-800 transition group"
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-slate-100">{t.title}</span>
                  {t.shortcut && (
                    <span className="text-[10px] font-mono bg-slate-800 text-[#25D366] px-1.5 py-0.5 rounded border border-slate-700">
                      {t.shortcut}
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-slate-400 line-clamp-2 mt-0.5">{t.content}</div>
              </button>
            ))}
          </motion.div>
        )}

        {open === "schedule" && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.15 }}
            className="absolute left-0 top-full mt-2 w-80 bg-slate-900 border border-slate-700 rounded-xl shadow-2xl z-30 p-3"
            data-testid="schedule-panel"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                Atalhos de agendamento
              </div>
              {googleConnected ? (
                <span className="inline-flex items-center gap-1 text-[9px] font-semibold text-[#25D366]">
                  <Check className="w-2.5 h-2.5" /> Google Agenda
                </span>
              ) : (
                <span className="text-[9px] text-slate-500">Conecte Google Agenda em Settings</span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              {scheduleShortcuts.map((s) => (
                <button
                  key={s.id || s.label}
                  data-testid={`schedule-slot-${s.label}`}
                  onClick={() => applySchedule(s)}
                  className="text-xs font-medium text-left px-2.5 py-2 rounded-md bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-[#25D366]/40 text-slate-200 transition flex items-center gap-1.5"
                >
                  <Clock className="w-3 h-3 text-[#25D366]" />
                  {s.label}
                </button>
              ))}
            </div>
            <p className="text-[10px] text-slate-500 mt-2">
              Gera a mensagem proposta. Conecte o Google Agenda em Settings para criar o evento automaticamente.
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
